<script>
import { orderBy as _orderBy } from 'lodash';
import { filter as _filter } from 'lodash';
import { includes as _includes } from 'lodash';
import { some as _some } from 'lodash';
import SingleSelector from "../../vue-app/components/SingleSelector.vue";
import SearchInput from "../../vue-app/components/SearchInput.vue";
import bus from '../utils/eventBus';
import normalizeDropdown from '../utils/dropdown';

export default {
  name: 'CareersListPage',
  components: {
    'single-selector': SingleSelector,
    'search-input': SearchInput
  },
  props: {
    openPositions: { type: Array, default: () => [] },
    countries: { type: Array, default: () => [] },
    jobFamilies: { type: Array, default: () => [] },
    cities: { type: Array, default: () => [] }
  },
  data() {
    return {
      bus: bus,
      selectedJobFamily: null,
      selectedCountry: null,
      selectedCity: null,
      filteredPositions: this.openPositions || [],
      filteredPositionsBatch: [],
      isLoading: false,
      page: 1,
      pageSize: 10,
      searchQuery: '',
      sortByProperty: 'Title',
      reverse: false
    };
  },
  computed: {
    showClearAll() {
      return ((this.selectedJobFamily && this.selectedJobFamily.value !== '-1') ||
        (this.selectedCountry && this.selectedCountry.value !== '-1') ||
        (this.selectedCity && this.selectedCity.value !== '-1')) ||
        this.searchQuery !== '';
    },
    showNoResults() {
      return this.filteredPositions.length === 0 && !this.isLoading;
    },
    showShowMore() {
      return (this.filteredPositions.length > this.page * this.pageSize) && !this.isLoading;
    },
    currentRoute() {
        return (this.$router && this.$router.currentRoute) || {};
    },
    currentQuery() {
        if (this.currentRoute.query) {
            return this.currentRoute.query;
        }
        const params = new URLSearchParams(window.location.search);
        const q = {};
        params.forEach((v, k) => { q[k] = v; });
        return q;
    }
  },
  watch: {
    filteredPositions() {
      this.filteredPositionsBatch = this.filteredPositions.slice(0, this.page * this.pageSize);
    }
  },
  mounted() {

    // Setup bus listener
    if (this.bus && typeof this.bus.$on === 'function') {
      this.bus.$on('dropdownChange', (val, id) => {
        if (id === 'countries') this.selectedCountry = normalizeDropdown(val, this.countries);
        else if (id === 'jobFamilies') this.selectedJobFamily = normalizeDropdown(val, this.jobFamilies);
        else if (id === 'cities') this.selectedCity = normalizeDropdown(val, this.cities);
        this.handleFilterChange();
      });
    }

    window.addEventListener('click', this.onOutsideClick);
    const query = this.currentQuery || {};
    this.filteredPositionsBatch = this.filteredPositions.slice(0, this.page * this.pageSize);
    if (query.jobFamily) {
        this.getPreselectedJobFamily(query.jobFamily);
    }
    if (query.country) {
        this.getPreselectedCountry(query.country);
    }
    if (query.city) {
        this.getPreselectedCity(query.city);
    }
    this.filterPositions(this.openPositions);
  },
  beforeUnmount() {
    window.removeEventListener('click', this.onOutsideClick);
  },
  methods: {
    // Treat 'All' (value '-1' or name 'All') as default/no-filter
    isDefaultSelection(sel) {
      if (!sel) return true;
      const norm = normalizeDropdown(sel) || {};
      const v = norm.value ?? norm.id ?? null;
      const n = norm.name ?? null;
      if (v === '-1' || v === -1) return true;
      if (String(n).toLowerCase() === 'all') return true;
      return false;
    },
    handleFilterChange() {
      this.page = 1;
      const query = {};
      if (!this.isDefaultSelection(this.selectedJobFamily)) query.jobFamily = this.selectedJobFamily.name || this.selectedJobFamily.id;
      if (!this.isDefaultSelection(this.selectedCountry)) query.country = this.selectedCountry.name || this.selectedCountry.id;
      if (!this.isDefaultSelection(this.selectedCity)) query.city = this.selectedCity.name || this.selectedCity.id;
      this.$router.push({ path: this.currentRoute.path || window.location.pathname, query: query }).catch(() => {});

      // Always filter from the full `openPositions` data to avoid progressive narrowing
      const source = this.openPositions || [];
      if (this.sortByProperty !== '') {
        this.filterPositions(_orderBy(source, (position) => this.getPositionsAttr(position), this.reverse ? 'desc' : 'asc'));
      } else {
        this.filterPositions(source);
      }
    },
    filterPositions(positions) {
      const freeText = (this.searchQuery || '').toLowerCase();
      this.filteredPositions = _filter(positions, position => {
        return position &&
          (this.isDefaultSelection(this.selectedJobFamily) || (position.jobFamily === (this.selectedJobFamily && (this.selectedJobFamily.name || this.selectedJobFamily.value || this.selectedJobFamily.id)))) &&
          (this.isDefaultSelection(this.selectedCountry) || (position.countries && _includes(position.countries, (this.selectedCountry && (this.selectedCountry.name || this.selectedCountry.value || this.selectedCountry.id))))) &&
          (this.isDefaultSelection(this.selectedCity) || (position.locations && _includes(position.locations, (this.selectedCity && (this.selectedCity.name || this.selectedCity.value || this.selectedCity.id))))) &&
          (freeText === '' || (
            (position.countries && _some(position.countries, s => s.toLowerCase().includes(freeText))) ||
            (position.jobFamily && position.jobFamily.toLowerCase().includes(freeText)) ||
            (position.locations && _some(position.locations, s => s.toLowerCase().includes(freeText))) ||
            (position.title && position.title.toLowerCase().includes(freeText)) ||
            (position.postingEndDate && position.postingEndDate.toLowerCase().includes(freeText))
          ));
      });
    },
    onSearchInput(val) {
      this.searchQuery = val;
      this.handleFilterChange();
    },
    handleClearAllClick() {
        this.clearAll();
    },
    clearAll() {
      this.bus.$emit('clearAll');
      this.searchQuery = '';
      this.selectedJobFamily = null;
      this.selectedCountry = null;
      this.selectedCity = null;
      this.page = 1;
      this.handleFilterChange();
    },
    getNextPositions() {
      this.page++;
      this.filteredPositionsBatch = this.filteredPositions.slice(0, this.page * this.pageSize);
    },
    getPreselectedJobFamily(val) {
      this.selectedJobFamily = normalizeDropdown({ id: val, name: val }, this.jobFamilies);
    },
    getPreselectedCountry(val) {
      this.selectedCountry = normalizeDropdown({ id: val, name: val }, this.countries);
    },
    getPreselectedCity(val) {
      this.selectedCity = normalizeDropdown({ id: val, name: val }, this.cities);
    },
    handleSortBy(val) {
      if (this.sortByProperty === val) {
        this.reverse = !this.reverse;
      } else {
        this.reverse = false;
        this.sortByProperty = val;
      }
      this.filterPositions(_orderBy(this.openPositions || [], (position) => this.getPositionsAttr(position), this.reverse ? 'desc' : 'asc'));
    },
    getPositionsAttr(position) {
      if (this.sortByProperty === 'locations' || this.sortByProperty === 'countries') {
        return position[this.sortByProperty];
      }
      return position[this.sortByProperty] ? position[this.sortByProperty].toLowerCase() : '';
    },
    takeFirst(stringArray, maxCount) {
      return (stringArray && stringArray.length) ? stringArray.slice(0, maxCount).join(', ') : '';
    },
    onOutsideClick(e) {
      this.bus.$emit('hideOtherDropdowns', e.target);
    }
  }
};

</script>
 